import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { ValidationController, ValidationRules, Validator } from 'aurelia-validation';
import { Models } from 'models/core';
import { DeleteDialogService } from 'services/delete-dialog-service';
import { ErrorService } from 'services/error-service';
import { NetService } from 'services/net-service';
import { ToastService } from 'services/toast-service';
import { connectTo } from 'aurelia-store';
import { ApplicationState } from 'lib/state';

@autoinject
@connectTo()
export class SpecialProductNew {
  private specialProduct: Models.Net;
  protected state: ApplicationState;
  protected isAdminUser: boolean = false;

  constructor(
    private deleteDialogService: DeleteDialogService,
    private errorService: ErrorService,
    private eventAggregator: EventAggregator,
    private i18n: I18N,
    private netService: NetService,
    private router: Router,
    private toastService: ToastService,
    private validationController: ValidationController,
    private validator: Validator
  ) {}

  private attached() {
    if (this.state.roles.includes('Administrator')) {
      this.isAdminUser = true;
    }
  }

  private activate(params) {
    this.netService
      .get(params.Id)
      .then((net) => {
        this.specialProduct = net;

        ValidationRules.ensure('CustomerId')
          .required()
          .withMessage(this.i18n.tr('general.requiredField'))
          .ensure('NetIdentifier')
          .required()
          .withMessage(this.i18n.tr('general.requiredField'))
          .ensure('ProducerId')
          .required()
          .withMessage(this.i18n.tr('general.producerField'))
          .ensure('SpecialProductTypeId')
          .required()
          .withMessage(this.i18n.tr('general.requiredField'))
          .on(this.specialProduct);
      })
      .catch((err) => this.errorService.handleError(err));
  }

  private validateBeforeSave() {
    // MANUAL VALIDATION
    this.validator.validateObject(this.specialProduct).then((result) => {
      const errors = result.filter((validateResult) => {
        return !validateResult.valid;
      });
      if (errors.length > 0) {
        this.validationController.validate();
      } else {
        this.updateNet();
      }
    });
  }

  private async updateNet() {
    try {
      const response = await this.netService.updateWithResponseMeta(this.specialProduct, this.specialProduct.Id);
      this.eventAggregator.publish('netListReset', 1);
      this.toastService.showSuccess('specialproduct.updated');

      const warning = response.headers.get('x-warning');
      if (warning) {
        this.toastService.showError('', undefined, decodeURIComponent(warning));
      }

      this.router.navigateToRoute('special-product-detail', { Id: this.specialProduct.Id });
    } catch (err) {
      this.errorService.handleError(err);
    }
  }

  private deleteSpecialProduct() {
    const msg = this.getDeleteSpecialProductMsg();
    this.deleteDialogService.confirmBeforeDelete(
      () => {
        this.netService
          .deleteNet(this.specialProduct.Id)
          .then((res) => {
            this.toastService.showSuccess('specialproduct.deleted');
            this.router.navigateToRoute('special-product-list');
          })
          .catch((err) => this.errorService.handleError(err));
      },
      'dialog.deleteHeading',
      this.specialProduct.AquacomId ? msg : 'dialog.deleteConfirmation',
      'dialog.yesDelete',
      'dialog.noCancel'
    );
  }

  private getDeleteSpecialProductMsg() {
    const infoText = this.i18n.tr('dialog.deleteConfirmation');
    const noticeText = this.i18n.tr('dialog.noticeAquacomSpecialProductDeletion');
    const noticeTextSpan = `<span style="color: red;">${noticeText}</span>`;
    const areYouSureText = this.i18n.tr('dialog.areYouSureContinue');
    return `${infoText} <br/><br/> ${noticeTextSpan} <br/><br/> ${areYouSureText} <br/><br/>`;
  }

  private gotoParentView() {
    this.eventAggregator.publish('netListReset', 1);
    this.router.navigateToRoute('special-product-list');
  }
}
